import { useState, useRef, useEffect, type FormEvent } from 'react'
import { Avatar } from './Avatar'
import { Button } from './Button'
import { Spinner } from './Spinner'
import { requireClient } from '@/lib/matrix/client'
import { useAuthStore } from '@/lib/store/auth-store'
import { fetchMediaUrl } from '@/lib/utils/decrypt-attachment'
import { Lightbox } from './Lightbox'

interface ProfileEditDialogProps {
  onClose: () => void
}

export function ProfileEditDialog({ onClose }: ProfileEditDialogProps) {
  const userId = useAuthStore((s) => s.userId)
  const client = requireClient()
  const user = client.getUser(userId!)

  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [mxcAvatar, setMxcAvatar] = useState(user?.avatarUrl ?? null)
  const [newAvatarFile, setNewAvatarFile] = useState<File | null>(null)
  const [newAvatarPreview, setNewAvatarPreview] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showFullAvatar, setShowFullAvatar] = useState(false)
  const [fullAvatarUrl, setFullAvatarUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (mxcAvatar) {
      fetchMediaUrl(mxcAvatar).then(setFullAvatarUrl).catch(() => {})
    }
  }, [mxcAvatar])

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setNewAvatarFile(file)
    setNewAvatarPreview(URL.createObjectURL(file))
    e.target.value = ''
  }

  const handleSave = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSaved(false)

    try {
      const nameChanged = displayName.trim() !== (user?.displayName ?? '')
      if (nameChanged) {
        await client.setDisplayName(displayName.trim())
      }

      if (newAvatarFile) {
        const upload = await client.uploadContent(newAvatarFile, { type: newAvatarFile.type })
        await client.setAvatarUrl(upload.content_uri)
        setMxcAvatar(upload.content_uri)
        setNewAvatarFile(null)
        setNewAvatarPreview(null)
      }

      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const localpart = userId?.replace(/^@(.+?):.*$/, '$1') ?? ''
  const domain = userId?.replace(/^@.+?:(.+)$/, '$1') ?? ''
  const avatarPreviewSrc = newAvatarPreview ?? fullAvatarUrl

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        className="w-full max-w-sm bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner */}
        <div className="h-20 bg-gradient-to-br from-[rgb(var(--np-accent)/0.3)] to-[rgb(var(--np-accent)/0.1)]" />

        {/* Avatar */}
        <div className="px-4 -mt-10">
          <div className="relative inline-block">
            <button
              onClick={() => { if (avatarPreviewSrc) setShowFullAvatar(true) }}
              className={avatarPreviewSrc ? 'cursor-zoom-in' : ''}
            >
              {avatarPreviewSrc ? (
                <img
                  src={avatarPreviewSrc}
                  alt={displayName}
                  className="w-20 h-20 rounded-full object-cover border-4 border-[rgb(var(--np-surface))]"
                />
              ) : (
                <Avatar
                  mxc={mxcAvatar}
                  name={displayName || localpart}
                  userId={userId ?? ''}
                  size={80}
                  className="border-4 border-[rgb(var(--np-surface))]"
                />
              )}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 p-1.5 bg-[rgb(var(--np-accent))] text-white rounded-full hover:bg-[rgb(var(--np-accent-muted))] transition-colors"
              title="Change avatar"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarSelect}
            />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="px-4 pt-3 pb-4">
          <p className="text-xs font-data text-[rgb(var(--np-tx-muted))] mb-3">
            @{localpart}<span className="text-[rgb(var(--np-tx-muted)/0.5)]">:{domain}</span>
          </p>

          <div className="mb-4">
            <label className="label-section mb-2 block">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2.5 text-sm text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)] transition-all"
              disabled={saving}
            />
          </div>

          {newAvatarFile && (
            <div className="flex items-center gap-2 mb-3 text-xs text-[rgb(var(--np-tx-muted))]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgb(var(--np-accent))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              New avatar selected: {newAvatarFile.name}
            </div>
          )}

          {error && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)] px-4 py-2 rounded-r mb-3">
              <p className="text-xs text-[rgb(var(--np-status-critical))]">{error}</p>
            </div>
          )}

          {saved && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-ok))] bg-[rgb(var(--np-status-ok)/0.05)] px-4 py-2 rounded-r mb-3">
              <p className="text-xs text-[rgb(var(--np-status-ok))]">Profile updated</p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
              {saved ? 'Done' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Spinner size={14} /> : 'Save'}
            </Button>
          </div>
        </form>
      </div>
      {showFullAvatar && avatarPreviewSrc && (
        <Lightbox src={avatarPreviewSrc} alt={displayName} onClose={() => setShowFullAvatar(false)} />
      )}
    </div>
  )
}
